import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { ConsoleShellSkeleton } from "./components/PageSkeletons";
import Landing from "./pages/Landing";
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ConsoleLayout = lazy(() => import("./components/ConsoleLayout"));
const Overview = lazy(() => import("./pages/Overview"));
const Employees = lazy(() => import("./pages/Employees"));
const EmployeeProgress = lazy(() => import("./pages/EmployeeProgress"));
const SessionSuggestions = lazy(() => import("./pages/SessionSuggestions"));
const Sops = lazy(() => import("./pages/Sops"));
const Devices = lazy(() => import("./pages/Devices"));
const Departments = lazy(() => import("./pages/Departments"));
const DepartmentEmployees = lazy(() => import("./pages/DepartmentEmployees"));
const Settings = lazy(() => import("./pages/Settings"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Terms = lazy(() => import("./pages/Terms"));
const Trust = lazy(() => import("./pages/Trust"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Protected({ children }) {
  const { user, ensureChecked, skipped } = useAuth();
  useEffect(() => { ensureChecked(); }, [ensureChecked]);
  if (user === null || skipped) return <ConsoleShellSkeleton />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <LanguageProvider>
      <AuthProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/trust" element={<Trust />} />
              <Route path="/console" element={<Protected><ConsoleLayout /></Protected>}>
                <Route index element={<Overview />} />
                <Route path="employees" element={<Employees />} />
                <Route path="employees/:id" element={<EmployeeProgress />} />
                <Route path="employees/:id/sessions/:sessionId" element={<SessionSuggestions />} />
                <Route path="sops" element={<Sops />} />
                <Route path="devices" element={<Devices />} />
                <Route path="departments" element={<Departments />} />
                <Route path="departments/:id" element={<DepartmentEmployees />} />
                <Route path="settings" element={<Settings />} />
              </Route>
            </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster
            position="top-right"
            gap={10}
            toastOptions={{
              unstyled: true,
              classNames: {
                toast: "flex items-start gap-3 w-full sm:max-w-sm bg-white border border-neutral-200 rounded-lg shadow-lg px-4 py-3.5 font-sans",
                icon: "shrink-0 mt-0.5",
                content: "flex-1 min-w-0",
                title: "text-sm font-medium text-[#111]",
                description: "text-xs text-neutral-500 mt-0.5 leading-relaxed",
                actionButton: "shrink-0 bg-[#111] text-white text-xs font-medium rounded-full px-3.5 py-1.5 hover:bg-neutral-800 transition-colors",
                cancelButton: "shrink-0 bg-neutral-100 text-neutral-600 text-xs font-medium rounded-full px-3.5 py-1.5 hover:bg-neutral-200 transition-colors",
                closeButton: "bg-white border border-neutral-200 text-neutral-400 hover:text-[#111] hover:bg-neutral-50",
                success: "border-l-2 border-l-green-600 [&_[data-icon]]:text-green-700",
                error: "border-l-2 border-l-red-600 [&_[data-icon]]:text-red-600",
                warning: "border-l-2 border-l-amber-500 [&_[data-icon]]:text-amber-600",
                info: "border-l-2 border-l-neutral-400 [&_[data-icon]]:text-neutral-500",
              },
            }}
          />
        </ConfirmProvider>
      </AuthProvider>
      </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
